import type { SupabaseClient } from '@supabase/supabase-js'

// ─── Action Payloads ──────────────────────────────────────────────────────

export interface ActionPayloadMap {
  // Phase 1 — Core combat
  RECEIVE_WOUND: {
    body_location: string
    laterality: string
    grade: string
    aspect: string
    effective_impact: number
    wound_type?: string
  }
  TREAT_WOUND: {
    injury_id: string
    healing_rate: number
    treatment_notes?: string
  }
  HEAL_WOUND: {
    injury_id: string
    new_grade?: string
    healed?: boolean
  }
  ADD_FATIGUE: { amount: number }
  REMOVE_FATIGUE: { amount: number }
  ADJUST_FUNDS: { delta: number; reason?: string }
  EQUIP_ARMOUR: { armour_id: string }
  UNEQUIP_ARMOUR: { armour_id: string }
  EQUIP_WEAPON: { weapon_id: string }
  UNEQUIP_WEAPON: { weapon_id: string }
  ABANDON_WEAPON: { weapon_id: string }
  ABANDON_ARMOUR: { armour_id: string }
  DRAW_WEAPON_TO_HAND: { weapon_id: string; hand: 'right' | 'left' }
  RELEASE_WEAPON_FROM_HAND: { weapon_id: string }
  DROP_WEAPON_FROM_HAND: { weapon_id: string }
  GRIP_WEAPON_TWO_HANDED: { weapon_id: string }

  // Phase 2 — Skills & conditions
  ADVANCE_SKILL: { skill_id: string; amount: number }
  OPEN_SKILL: { skill_name: string; sb: number; oml: number; max_ml: number }
  APPLY_CONDITION: { condition: string }
  REMOVE_CONDITION: { condition: string }
  ADD_BLOODLOSS: { amount: number }
  REDUCE_BLOODLOSS: { amount: number }

  // Phase 3 — Equipment management
  ACQUIRE_EQUIPMENT: { item_name: string; quantity?: number; weight_lbs?: number; value_denarii?: number; location?: string; parent_item_id?: string }
  DROP_EQUIPMENT: { equipment_id: string }
  MOVE_EQUIPMENT: { equipment_id: string; location: string }
  USE_ITEM: { equipment_id: string; amount?: number }
  PUT_IN_CONTAINER: { equipment_id: string; container_id: string }
  REMOVE_FROM_CONTAINER: { equipment_id: string }

  // Phase 4 — Advanced
  INFECT_WOUND: { injury_id: string; infection_hr: number }
  BIOGRAPHY_ENTRY: { event_label: string; character_age: number; game_year?: number; flavour_text?: string; is_gm_only?: boolean }
}

export type ActionType = keyof ActionPayloadMap

// ─── Effect Record ────────────────────────────────────────────────────────

export interface EffectRecord {
  character_id: string
  target_table: string
  target_row_id: string | null
  target_field: string
  operation: 'INSERT' | 'UPDATE' | 'DELETE'
  old_value: unknown
  new_value: unknown
}

// ─── Handler Context & Interface ──────────────────────────────────────────

export interface ActionContext {
  characterId: string
  campaignId: string
  supabase: SupabaseClient
}

export interface ActionHandlerResult {
  effects: EffectRecord[]
  label: string
  description?: string
  undoPolicy?: 'undoable' | 'gm_only_undo' | 'permanent' | 'corrective'
}

export type ActionHandler<T extends ActionType> = (
  ctx: ActionContext,
  payload: ActionPayloadMap[T]
) => Promise<ActionHandlerResult>

// ─── Grade → Injury Levels ────────────────────────────────────────────────

export const GRADE_LEVELS: Record<string, number> = {
  M1: 1, S2: 2, S3: 3, G4: 4, G5: 5, K4: 4, K5: 5,
}
